/**
 * The scanner's API client. Same origin (the Worker forwards /api/* over a
 * service binding), same cookies as the box office: `__Host-tix_device`
 * from pairing, `__Host-tix_staff` from the PIN login. Nothing here is
 * cached by the service worker; offline is the bundle in IndexedDB.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
  /** No response at all: the network is down or the API unreachable. */
  get isOffline(): boolean {
    return this.status === 0;
  }
}

async function toError(response: Response): Promise<ApiError> {
  let body: { error?: { code?: string; message?: string; details?: Record<string, unknown> } } = {};
  try {
    body = await response.json();
  } catch {
    // non-JSON
  }
  return new ApiError(response.status, body.error?.code ?? 'UNKNOWN', body.error?.message ?? `Request failed (${response.status})`, body.error?.details);
}

async function request<T>(path: string, init: RequestInit = {}, timeoutMs = 8000): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      credentials: 'same-origin',
      signal: controller.signal,
      headers: { accept: 'application/json', ...(init.body ? { 'content-type': 'application/json' } : {}), ...(init.method && init.method !== 'GET' ? { 'x-bre-csrf': '1' } : {}), ...(init.headers as Record<string, string> | undefined) },
    });
  } catch (cause) {
    throw new ApiError(0, 'OFFLINE', cause instanceof Error && cause.name === 'AbortError' ? 'The API did not answer in time' : 'No connection');
  } finally {
    clearTimeout(timer);
  }
  if (!response.ok) throw await toError(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string, timeoutMs?: number) => request<T>(path, {}, timeoutMs),
  post: <T>(path: string, body?: unknown, timeoutMs?: number) => request<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) }, timeoutMs),
};

// --- Shapes ------------------------------------------------------------------------

export interface SessionResponse {
  staff: { id: string; display_name: string; status: string };
  session: { id: string; surface: string; device_id: string | null; expires_at: string };
  permissions: string[];
}

export type ScanResult = 'VALID' | 'RE_SCAN' | 'ALREADY_SCANNED' | 'INVALID' | 'WRONG_EVENT' | 'WRONG_PERFORMANCE' | 'WRONG_ACCESS_ZONE' | 'NOT_YET_VALID' | 'EXPIRED' | 'REFUNDED' | 'REVOKED' | 'TRANSFERRED' | 'MANUAL_OVERRIDE' | 'OFFLINE_ACCEPTED' | 'OFFLINE_CONFLICT';
export type ScanColor = 'GREEN' | 'YELLOW' | 'RED';
export type InputMethod = 'CAMERA' | 'HID_SCANNER' | 'MANUAL_CODE';

export interface ScanPerformance {
  id: string;
  event_id: string;
  title: string;
  starts_at: string;
  doors_at: string | null;
  ends_at: string | null;
  timezone: string;
  status: string;
  reentry_policy: string;
  admission_opens_at: string | null;
  admission_closes_at: string | null;
  gate_zones: Record<string, string[]>;
  counters: ScanCounters;
}

export interface ScanCounters {
  gate_admitted: number;
  performance_admitted: number;
  performance_live_tickets: number;
}

export interface ScannerContext {
  device: { id: string; name: string; venue_id: string | null };
  staff: { id: string; display_name: string };
  server_time: string;
  gates: { id: string; code: string; name: string }[];
  zones: { id: string; code: string; name: string }[];
  performances: ScanPerformance[];
  can_override: boolean;
  can_bundle: boolean;
}

export interface ScanOutcome {
  scan_id: string;
  result: ScanResult;
  color: ScanColor;
  counts_as_admission: boolean;
  reason_code: string | null;
  reason_text: string | null;
  ticket: { id: string; short_code: string; ticket_type: string | null; seat_label: string; holder_name: string | null; zone_id: string | null; credential_version: number } | null;
  previous: { scan_id: string; at: string; gate: string | null; device: string; staff: string | null } | null;
  counters: ScanCounters;
  /** The season pass presented, when it was one (D-373). */
  season_pass?: { id: string; label: string; number: string; holder_name: string | null } | null;
  /** The ticket's entry time and how this arrival sits against it (D-86, PART 35). */
  timed_entry?: { slot_id: string; starts_at: string; arrival: 'EARLY' | 'ON_TIME' | 'LATE'; minutes_from_window: number } | null;
  /** Set by the app, never by the server: the decision was made from the bundle. */
  offline?: boolean;
}

export interface BundleTicket {
  tid: string;
  cv: number;
  status: string;
  short_code: string;
  type: string | null;
  seat: string;
  zones: string[] | null;
  holder: string | null;
  admitted: { at: string; gate: string | null; device: string } | null;
  updated_at: string;
  /** The season pass this ticket was made for (D-373). Absent from an older API. */
  pass?: string | null;
}

/** A season pass as the bundle carries it (D-373). */
export interface BundlePass {
  id: string;
  cv: number;
  status: string;
  label: string;
  valid_from: string;
  valid_to: string;
  updated_at: string;
}

export interface BundleSeasonPasses {
  season_id: string;
  season_name: string;
  game_date: string;
  key: { generation: number; raw: string };
  passes: BundlePass[];
}

export interface Bundle {
  bundle_id: string;
  bundle_version: number;
  generated_at: string;
  expires_at: string;
  performance: { id: string; event_id: string; title: string; starts_at: string; ends_at: string | null; timezone: string; reentry_policy: string; admission_opens_at: string | null; admission_closes_at: string | null };
  verification_key: { generation: number; raw: string };
  gate_zones: Record<string, string[]>;
  tickets: BundleTicket[];
  /** Whether this device may admit from the bundle with no network (D-329). Absent from an older API: on. */
  offline_admission?: boolean;
  /** Tonight's season passes and the key to verify them (D-373); null when passes do not cover the game. */
  season_passes?: BundleSeasonPasses | null;
}

export interface BundleDelta {
  bundle_id: string;
  generated_at: string;
  revoked: boolean;
  expires_at: string;
  tickets: BundleTicket[];
  offline_admission?: boolean;
  season_passes?: BundlePass[];
}

export interface OfflineScanUpload {
  scan_id: string;
  ticket_id: string | null;
  credential_version_presented: number | null;
  performance_id: string;
  gate_id: string | null;
  direction: 'ENTRY' | 'EXIT';
  input_method: InputMethod;
  device_result: ScanResult;
  reason_code: string | null;
  raw_sha256: string;
  device_timestamp: string;
  device_clock_offset_ms: number | null;
  bundle_id: string | null;
  /** A season pass admitted offline (D-373): the server makes its ticket on sync. */
  season_pass_id?: string | null;
}

export interface SyncOutcome {
  batch_id: string;
  already_processed: boolean;
  results: { scan_id: string; result: ScanResult; reason_code: string | null; previous_scan_id: string | null; duplicate: boolean }[];
  accepted: number;
  conflicts: number;
}

export const endpoints = {
  session: () => api.get<SessionResponse>('/api/auth/session'),
  logout: () => api.post('/api/auth/logout'),
  pair: (code: string) => api.post<{ device: { id: string; name: string; device_type: string }; token: string }>('/api/devices/pair', { code }),
  pinLogin: (pin: string) => api.post<{ staff: { id: string; display_name: string }; surface: string }>('/api/auth/pin', { pin }),
  context: () => api.get<ScannerContext>('/api/scanner/context'),
  time: () => api.get<{ server_time: string }>('/api/scanner/time', 4000),
  counters: (performanceId: string, gateId: string | null) => api.get<{ counters: ScanCounters }>(`/api/scanner/performances/${performanceId}/counters${gateId ? `?gate_id=${gateId}` : ''}`, 4000),
  scan: (input: { scan_id: string; raw: string; performance_ids: string[]; gate_id: string | null; direction: 'ENTRY' | 'EXIT'; input_method: InputMethod; device_timestamp: string }) => api.post<ScanOutcome>('/api/scanner/scans', input, 5000),
  override: (scanId: string, input: { manager_pin: string; reason: string }) => api.post<ScanOutcome>(`/api/scanner/scans/${scanId}/override`, input),
  arrivalOverride: (scanId: string, input: { reason: string }) => api.post<ScanOutcome>(`/api/scanner/scans/${scanId}/arrival-override`, input),
  sync: (input: { client_batch_id: string; scans: OfflineScanUpload[] }) => api.post<SyncOutcome>('/api/scanner/scans/sync', input, 20_000),
  bundle: (performanceId: string) => api.get<{ bundle: Bundle }>(`/api/scanner/performances/${performanceId}/bundle`, 60_000),
  delta: (bundleId: string, since: string) => api.get<{ delta: BundleDelta }>(`/api/scanner/bundles/${bundleId}/delta?since=${encodeURIComponent(since)}`, 15_000),
};

export function when(value: string | null | undefined, timeZone = 'America/Chicago', style: 'long' | 'short' | 'time' = 'short'): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const options: Intl.DateTimeFormatOptions = style === 'time' ? { timeZone, hour: 'numeric', minute: '2-digit' } : style === 'long' ? { timeZone, weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' } : { timeZone, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' };
  return new Intl.DateTimeFormat('en-US', options).format(date);
}
